/** Bulk "Security" — append to / clone the WAF access-control lists. One section
 * per list (whitelist / blacklist); each toggles CIDR-IP vs Geo and either adds
 * new values or clones the list from another domain. */
import { useMemo, useState } from 'react';
import {
  Input,
  SegmentedControl,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { useWafDomains } from '@/api/waf';
import { geoCountries, acGeoCode, countryName } from '../_shared/geo';
import { BulkSection } from './BulkSection';
import { useBulkApply } from './useBulkApply';
import styles from './WafBulkConfigure.module.css';

const splitValues = (s: string) =>
  Array.from(new Set(s.split(/[\n,]/).map((x) => x.trim()).filter(Boolean)));

/** Countries as access-control geo options (value = full `XX-YY-00-00-00` code), label-sorted. */
const GEO_OPTIONS = geoCountries
  .map((c) => ({ value: acGeoCode(c), label: countryName(c.code) }))
  .sort((a, b) => a.label.localeCompare(b.label));

/** One access-control list (white | black) with CIDR/Geo + add-new/clone-from. */
function AclSection({
  serviceId,
  domains,
  title,
  listType,
}: {
  serviceId: string;
  domains: string[];
  title: string;
  listType: 'white' | 'black';
}) {
  const [kind, setKind] = useState<'ip' | 'geo'>('ip');
  const [mode, setMode] = useState<'new' | 'clone'>('new');
  const [values, setValues] = useState(''); // IP/CIDR free-text
  const [geo, setGeo] = useState(''); // single country geo code
  const [copyFrom, setCopyFrom] = useState('');
  const ac = useBulkApply(serviceId, 'access-control');
  const domainsQ = useWafDomains(serviceId, { per_page: 200 });
  const allDomains = useMemo(() => (domainsQ.data?.items ?? []).map((d) => d.domain), [domainsQ.data]);

  const list = `${kind}-${listType}`;
  // New-mode values: a single country for geo, else the parsed IP/CIDR list.
  const newValues = kind === 'geo' ? (geo ? [geo] : []) : splitValues(values);
  const apply = () => {
    if (mode === 'clone') {
      if (copyFrom) ac.mutate({ domains: domains.filter((d) => d !== copyFrom), list, action: 'copy', copy_from: copyFrom });
    } else if (newValues.length) {
      ac.mutate({ domains, list, action: 'append', values: newValues });
    }
  };
  const disabled = mode === 'clone' ? !copyFrom : newValues.length === 0;

  return (
    <BulkSection title={title} onApply={apply} disabled={disabled} pending={ac.isPending}>
      <div className={styles.inline}>
        <SegmentedControl
          items={[{ value: 'ip', label: 'CIDR/IP' }, { value: 'geo', label: 'Geo' }]}
          value={kind}
          onValueChange={(v) => setKind(v as 'ip' | 'geo')}
          aria-label="ACL kind"
        />
      </div>
      <RadioGroup
        value={mode}
        onValueChange={(v) => setMode(v as 'new' | 'clone')}
        style={{ display: 'flex', flexDirection: 'row', gap: 'var(--vn-space-4)' }}
      >
        <label className={styles.row} style={{ gap: 6, cursor: 'pointer' }}>
          <RadioGroupItem value="new" /> Add new
        </label>
        <label className={styles.row} style={{ gap: 6, cursor: 'pointer' }}>
          <RadioGroupItem value="clone" /> Clone from
        </label>
      </RadioGroup>

      {mode === 'new' ? (
        kind === 'geo' ? (
          <Select value={geo} onValueChange={setGeo}>
            <SelectTrigger aria-label="Country">
              <SelectValue placeholder="Select a country" />
            </SelectTrigger>
            <SelectContent>
              {GEO_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            placeholder="CIDR / IP"
            value={values}
            onChange={(e) => setValues(e.target.value)}
          />
        )
      ) : (
        <Select value={copyFrom} onValueChange={setCopyFrom} disabled={domainsQ.isLoading}>
          <SelectTrigger aria-label="Clone from domain">
            <SelectValue placeholder={domainsQ.isLoading ? 'Loading…' : 'Domain'} />
          </SelectTrigger>
          <SelectContent>
            {allDomains.map((d) => (
              <SelectItem key={d} value={d}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </BulkSection>
  );
}

export function SecurityTab({ serviceId, domains }: { serviceId: string; domains: string[] }) {
  return (
    <>
      <AclSection serviceId={serviceId} domains={domains} title="ACL-Whitelist" listType="white" />
      <AclSection serviceId={serviceId} domains={domains} title="ACL-Blacklist" listType="black" />
    </>
  );
}

export default SecurityTab;
